/* eslint-disable react/no-unescaped-entities */
/* eslint-disable consistent-return */
import React, { useEffect, useRef, useState } from 'react';
import {
  MonitoringBiddingTypes,
  MonitoringFilter,
  MonitoringRequestTypes,
  MonitoringState,
  TradingMessageTypes,
} from 'store/modules/monitoring_store/types';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import TradingItem from 'components/molecules/trading_item';
import TradingContentItem from 'components/molecules/trading_content_item';
import TradingMessages from 'components/molecules/trading_messages';
import {
  getAppealsNotifications,
  getElectronicsTradings,
  getTradingMessages,
} from 'store/modules/monitoring_store/actions';
import { Link } from 'react-router-dom';
import TradingMessageAppeals from 'components/molecules/trading_message_appeals';
import { PanelState } from 'store/modules/panel_store/types';
import Pagination from 'components/atoms/pagination';
import { ProfileState, ProfileTypes } from 'store/modules/profile_store/types';
import tabId from 'services/tabId';
import companyId from 'services/company_id';
import { monitoringCompanies, tradingOwnerCompany } from 'services/monitoring_companies';
import createTrack from 'services/actions/tracker_actions';
import MonitoringPortalsListModal from 'components/molecules/monitoring_portals_list_modal';
import OpenTutorial from 'components/atoms/open_tutorial';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ChatMessageSearch from 'components/organisms/chat_message_search';
import EmptyState from 'components/atoms/empty_state';
import OverviewControl from 'components/organisms/overview_control';
import { ManagementState, RequestState } from 'store/modules/management_store/types';
import { getBiddingOverview } from 'store/modules/management_store/actions';
import ByMeByAllGroup from '../by_me_by_all_group';
import TradingPortalFilters from '../trading_portal_filters';
import MonitoringFilterTabs from '../monitoring_filter_tabs';

interface TradingListProps {
  isTradingMessage: boolean;
  setIsTradingMessage: React.Dispatch<React.SetStateAction<boolean>>;
  search: string | null;
  setSearch: React.Dispatch<React.SetStateAction<string | null>>;
  activeFilter: MonitoringFilter;
  setActiveFilter: React.Dispatch<
    React.SetStateAction<'all' | 'unread' | 'important' | 'archived'>
  >;
}

const TradingList = (props: TradingListProps) => {
  const {
    isTradingMessage,
    setIsTradingMessage,
    search,
    setSearch,
    activeFilter,
    setActiveFilter,
  } = props;
  const TIMER_RELOAD = 120000;
  const POSTS_PER_PAGE = 15;
  const dispatch = useDispatch();
  const [modalListOpened, setModalListOpened] = useState<boolean>(false);
  const [toggleMobile, setToggleMobile] = useState<boolean>(false);
  const [overviewOpen, setOverviewOpen] = useState<boolean>(false);
  const { panel } = useSelector<ApplicationState, PanelState>((state) => state.session.panel);
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [showSearchMessage, setShowSearchMessage] = useState<boolean>(false);
  const [messageSearch, setMessageSearch] = useState<string>('');
  const [scrollTargetMessageId, setScrollTargetMessageId] = useState<number | null>(null);
  const [searchResults, setSearchResults] = useState<TradingMessageTypes[]>([]);

  const { monitoring, request } = useSelector<ApplicationState, MonitoringState>(
    (state) => state.storage.monitoring,
  );
  const { management } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const management_request = useSelector<ApplicationState, RequestState>(
    (state) => state.session.management.request,
  );

  const findSelectedElectronicTrading = (tradingId: number) => {
    if (tradingId === 0) return monitoring.electronics_tradings[0];

    return monitoring.electronics_tradings.find(
      (electronicTrading) => electronicTrading.id === tradingId,
    );
  };

  const updateCurrentPage = (page: number = 1) => {
    dispatch({ type: MonitoringRequestTypes.UPDATE_CURRENT_PAGE, page });
  };

  const getSelectedTradingId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const electronicTradingIdParam = urlParams.get('electronic_trading_id');

    if (electronicTradingIdParam) return parseInt(electronicTradingIdParam, 10);

    if (monitoring.electronics_tradings?.length > 0) return monitoring.electronics_tradings[0].id;

    return 0;
  };

  const [selectedTradingId, setSelectedTradingId] = useState<number>(getSelectedTradingId());
  const [selectedElectronicTrading, setSelectedElectronicTrading] = useState<
    MonitoringBiddingTypes | undefined
  >(findSelectedElectronicTrading(selectedTradingId));

  const profileRef = useRef(profile);
  profileRef.current = profile;

  const fetchTradings = (override?: Partial<any>) => {
    const { current } = profileRef;
    const activeCompany = current.representant ? current.currentCompany[tabId()] : current.clientId;

    dispatch(
      getElectronicsTradings({
        page: monitoring?.current_page,
        source_id: monitoring?.current_source_id,
        filtered_by: monitoring?.filtered_by,
        companyId: activeCompany,
        companyIds: monitoringCompanies(current, activeCompany),
        filter_type: activeFilter,
        search,
        ...override,
      }),
    );
  };

  const ownerCompany = tradingOwnerCompany(
    profile,
    findSelectedElectronicTrading(selectedTradingId),
  );

  useEffect(() => {
    if (ownerCompany === null) return;

    dispatch({
      type: ProfileTypes.UPDATE_CURRENT_COMPANY,
      tabId: tabId(),
      companyId: ownerCompany,
    });
  }, [ownerCompany, selectedTradingId]);
  useEffect(() => {
    if (!overviewOpen) return;
    if (!selectedElectronicTrading?.bidding_id) return;

    const biddingChanged = management.overview_bidding_id !== selectedElectronicTrading.bidding_id;

    const now = Date.now();
    const lastUpdate = management.overview_last_updated || 0;
    const FIVE_MINUTES = 5 * 60 * 1000;
    const cacheExpired = !management.overview || now - lastUpdate > FIVE_MINUTES;

    if (biddingChanged || cacheExpired) {
      dispatch(getBiddingOverview(selectedElectronicTrading.bidding_id));
    }
  }, [overviewOpen, selectedElectronicTrading?.bidding_id]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchTradings();
      dispatch(getAppealsNotifications(companyId(profileRef.current)));
    }, TIMER_RELOAD);

    return () => clearInterval(interval);
  }, [monitoring?.current_page, monitoring?.current_source_id]);

  useEffect(() => {
    setSelectedElectronicTrading(findSelectedElectronicTrading(selectedTradingId));
  }, [selectedTradingId, monitoring.electronics_tradings]);

  useEffect(() => {
    if (panel.partner_id && panel.partner_id > 0)
      dispatch({
        type: MonitoringRequestTypes.UPDATE_CURRENT_SOURCE_ID,
        sourceId: panel.partner_id,
      });
  }, [panel.partner_id]);

  const fetchedForLastMessageTime = useRef<Record<number, string>>({});

  const newestLoadedMessageTime = (electronicTradingId: number) => {
    const messages = monitoring.monitoring_messages[electronicTradingId];

    if (!messages || messages.length === 0) return null;

    return messages.reduce((newest: number, message: TradingMessageTypes) => {
      const messageTime = Date.parse(message.message_time);

      return Number.isNaN(messageTime) || messageTime < newest ? newest : messageTime;
    }, 0);
  };

  useEffect(() => {
    if (!selectedElectronicTrading?.id) return;

    const tradingId = selectedElectronicTrading.id;
    const cardMessageTime = selectedElectronicTrading.last_message_time;

    const newestLoaded = newestLoadedMessageTime(tradingId);
    const parsedCardMessageTime = Date.parse(cardMessageTime);

    const hasNewerMessage =
      newestLoaded !== null &&
      !Number.isNaN(parsedCardMessageTime) &&
      parsedCardMessageTime > newestLoaded &&
      fetchedForLastMessageTime.current[tradingId] !== cardMessageTime;

    if (newestLoaded === null || selectedElectronicTrading.have_new_messages || hasNewerMessage) {
      fetchedForLastMessageTime.current[tradingId] = cardMessageTime;
      dispatch(getTradingMessages(tradingId));
    }
  }, [selectedElectronicTrading]);

  const toggleSelectedTrading = (tradingId: number) => {
    setSelectedTradingId(tradingId);
    setIsTradingMessage(true);
    setToggleMobile(true);
  };

  const handleFilterBy = () => {
    dispatch({ type: MonitoringRequestTypes.HANDLE_FILTER_BY });
  };

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="monitoring px-3 position-relative">
          <div className="row">
            <div className="col-md-3 p-0 bg-white border-right monitoring-sidebar">
              <div className="d-flex justify-content-between align-items-center my-3 px-3">
                <h3 className="mb-0">Monitorar Chat</h3>
                <div className="d-flex align-items-end align-items-stretch">
                  <OpenTutorial type="modal" source="monitoring" />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mr-1 d-flex align-items-center position-relative databalloon"
                    aria-label="Conheça todos os portais disponíveis na plataforma ConLicitação"
                    data-balloon-pos="down"
                    data-balloon-blunt
                    data-toggle="modal"
                    data-target="#monitoring-portals-modal"
                    onClick={() => {
                      setModalListOpened(true);
                      createTrack('Monitorar Chat - Modal de portais');
                    }}
                  >
                    <span className="material-icons">business</span>
                  </button>
                  <button
                    className="btn btn-secondary btn-sm mr-1 d-flex align-items-center"
                    type="button"
                    data-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span className="material-icons">more_vert</span>
                  </button>
                  <div className="dropdown-menu dropdown-menu-right">
                    <Link className="dropdown-item" to="/facilitadores/trading_keywords">
                      Configurações gerais
                    </Link>
                  </div>
                </div>
              </div>
              <ByMeByAllGroup
                byMe={monitoring.filtered_by === 'by_me'}
                byAll={monitoring.filtered_by === 'by_all'}
                handle={handleFilterBy}
                setSearch={setSearch}
                search={search}
              />
              <TradingPortalFilters />
              <MonitoringFilterTabs value={activeFilter} onChange={setActiveFilter} />
              <div className="list-group trading-list mt-2 pl-3">
                {request?.electronics_tradings?.loading ? (
                  <Skeleton count={7} height={60} />
                ) : monitoring.electronics_tradings?.length === 0 ? (
                  search && search.trim().length > 0 ? (
                    <EmptyState
                      icon="search_off"
                      title={`Não encontramos resultados para "${search}"`}
                      description="Verifique o número do pregão ou edital."
                    />
                  ) : activeFilter === 'important' ? (
                    <EmptyState
                      icon="star_border"
                      title="Nenhum pregão marcado como importante"
                      description="Marque um pregão como importante para vê-lo aqui."
                    />
                  ) : activeFilter === 'archived' ? (
                    <EmptyState
                      icon="archive"
                      title="Nenhum pregão arquivado"
                      description="Os pregões arquivados aparecerão aqui."
                    />
                  ) : (
                    <EmptyState icon="inbox" title="Nenhum pregão encontrado" />
                  )
                ) : (
                  monitoring.electronics_tradings?.map((electronicTrading, tradingPosition) => (
                    <TradingItem
                      key={electronicTrading.id}
                      trading={electronicTrading}
                      toggleSelectedTrading={toggleSelectedTrading}
                      isSelectedTrading={electronicTrading.id === selectedTradingId}
                      showMonitoredByBalloon={monitoring?.filtered_by === 'by_all'}
                      monitoredByList={electronicTrading.monitored_by}
                      tradingPosition={tradingPosition}
                      onCompaniesChanged={() => fetchTradings()}
                    />
                  ))
                )}
              </div>
              <div className="d-flex justify-content-center border-top py-2">
                <Pagination
                  postsPerPage={POSTS_PER_PAGE}
                  currentPage={monitoring?.current_page || 1}
                  paginate={updateCurrentPage}
                  totalPosts={monitoring?.electronics_tradings_total_entries}
                />
              </div>
            </div>
            <div
              className={`monitoring-bidding px-sm-0 ${toggleMobile && 'open-mobile'} ${
                showSearchMessage ? 'col-md-6' : 'col-md-9'
              }`}
            >
              {selectedElectronicTrading &&
              ownerCompany !== null ? null : selectedElectronicTrading ? (
                <>
                  <div className="border-bottom d-md-none d-block py-2 monitoring-back">
                    <button
                      type="button"
                      className="btn btn-link btn-sm d-flex align-items-center"
                      onClick={() => {
                        setToggleMobile(false);
                      }}
                    >
                      <i className="material-icons">keyboard_arrow_left</i>
                      Voltar para a lista de pregões monitorados
                    </button>
                  </div>

                  <TradingContentItem
                    trading={selectedElectronicTrading}
                    selectedTradingId={selectedTradingId}
                    setShowSearchMessage={setShowSearchMessage}
                    setOverviewOpen={setOverviewOpen}
                    onCompaniesChanged={() => fetchTradings()}
                  />

                  {isTradingMessage ? (
                    <TradingMessages
                      key={selectedTradingId}
                      selectedTradingId={selectedTradingId}
                      messageSearch={messageSearch}
                      scrollTargetMessageId={scrollTargetMessageId}
                      setSearchResults={setSearchResults}
                    />
                  ) : (
                    <TradingMessageAppeals
                      key={selectedTradingId}
                      electronicTradingId={selectedTradingId}
                    />
                  )}
                </>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-center">
                  <div>
                    <i className="material-icons mb-2" style={{ fontSize: 40, color: '#ccc' }}>
                      gavel
                    </i>
                    <p className="mb-0 text-muted">
                      Selecione um pregão para visualizar as informações
                    </p>
                  </div>
                </div>
              )}
            </div>
            {showSearchMessage && (
              <div className="col-md-3 p-0">
                <ChatMessageSearch
                  setShowSearchMessage={setShowSearchMessage}
                  keywords={(monitoring.trading_keywords[companyId(profile)] || []).filter(
                    (keyword) => keyword.active !== false,
                  )}
                  setMessageSearch={setMessageSearch}
                  setScrollTargetMessageId={setScrollTargetMessageId}
                  searchResults={searchResults}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <MonitoringPortalsListModal
        modalListOpened={modalListOpened}
        setModalListOpened={setModalListOpened}
      />
      {overviewOpen && (
        <OverviewControl
          loading={management_request?.get_bidding_overview?.loading}
          onClose={() => setOverviewOpen(false)}
          sections={[
            { type: 'situacao', data: management?.overview?.situacao_atual },
            { type: 'resumo', data: management?.overview?.chat_resumo },
            { type: 'chat', data: management?.overview?.chat_principais, hidden: true },
            { type: 'andamentos', data: management?.overview?.andamentos, hidden: true },
            { type: 'anotacoes', data: management?.overview?.anotacoes },
          ]}
        />
      )}
    </div>
  );
};

export default TradingList;
